import type { Translated } from '@uefadigital/panenka';
import clsx from 'clsx';
import { lazy, memo, Suspense, useMemo } from 'react';

import { useDrawExtracted } from '../../hooks';
import { useDrawOptions, useDrawStore } from '../../store';

import { useWindowSize } from '@hooks/useWindowSize';
import TeamIdentifier from '@shared/Identifier/TeamIdentifier';
import { useTranslateFn } from '@shared/Translate';
import type { Team } from '@shared-types/match.types';
import { RoundMode } from '@shared-types/match.types';

import * as styles from './styles.module.scss';

const FollowButton = lazy(() => import('../FollowButton'));

const DrawTeam = ({
  team,
  isResult = false,
  badgeSlot = 'prefix',
  alignment = 'left',
}: {
  team: Translated<Team>;
  isResult?: boolean;
  badgeSlot?: 'suffix' | 'prefix';
  alignment?: 'center' | 'left' | 'right';
}) => {
  const mode = useDrawStore((state) => state.draw?.rounds?.[0].round.mode);
  const phase = useDrawStore((state) => state.draw?.rounds?.[0].round.phase);
  const { extracted, extracting } = useDrawExtracted(team?.id, isResult);

  const t = useTranslateFn();
  const { isMobile } = useWindowSize();
  const {
    options: { enableTeamLink, enableFavouriteClub },
  } = useDrawOptions();

  const badgeSize = useMemo(() => {
    // UNL
    if (window.competitionId === '2004') {
      // POTS
      if (!isResult) return 29;

      // GROUPS RESULTS
      if (mode === RoundMode.GROUP) return isMobile ? 48 : 29;
    }

    // POTS
    if (!isResult) return 32;

    // GROUPS RESULTS
    if (mode === RoundMode.GROUP) return isMobile ? 40 : 24;

    // RESULTS
    return 32;
  }, [isMobile, isResult, mode]);

  const orientation = useMemo(
    () => (isResult && mode === RoundMode.GROUP && isMobile ? 'vertical' : 'horizontal'),
    [isMobile, isResult, mode],
  );

  const fallbackName = t(window.isClub ? 'club' : 'team');
  const fallbackTeam = useMemo(() => ({ id: '', displayName: fallbackName }), [fallbackName]);

  return (
    <div
      pk-theme={extracting ? 'dark' : null}
      className={clsx({
        [styles.team]: true,
        [styles.teamPot]: !isResult,
        [styles.teamResult]: isResult,
        [styles.extracted]: extracted,
        [styles.extracting]: extracting,
        [styles[`mode--${mode?.toLowerCase()}`]]: !!mode,
      })}
    >
      <TeamIdentifier
        className={styles.identifier}
        wrapperClass='pk-w--100'
        team={team ?? fallbackTeam}
        badgeSize={badgeSize}
        badgeSlot={badgeSlot}
        type={orientation}
        showTeamCountry={false}
        alignment={alignment}
        enableLink={!!(team && !team.isPlaceHolder && enableTeamLink)}
        phase={phase}
      />
      {!isResult && enableFavouriteClub && (
        <Suspense>
          <FollowButton team={team} className={styles.followBtn} />
        </Suspense>
      )}
    </div>
  );
};

export default memo(DrawTeam);
